import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Form } from 'antd'
import { AUTH_PRIMARY_BUTTON_CLASS } from '@/features/auth/constants'
import AuthenticatorCodeInput from '@/features/auth/components/AuthenticatorCodeInput'
import { isCompleteAuthenticatorCode } from '@/features/auth/authenticatorCode'

type MfaCodeStepProps = {
  email: string
  loading: boolean
  error: string | null
  onVerify: (code: string) => void
  onBack: () => void
}

export default function MfaCodeStep({ email, loading, error, onVerify, onBack }: MfaCodeStepProps) {
  const [code, setCode] = useState('')
  const [attempt, setAttempt] = useState(0)
  const wasLoading = useRef(false)

  // Codes are single use: after each attempt the boxes start empty again.
  useEffect(() => {
    if (wasLoading.current && !loading) {
      setCode('')
      setAttempt((value) => value + 1)
    }
    wasLoading.current = loading
  }, [loading])

  const submit = (value: string) => {
    if (loading || !isCompleteAuthenticatorCode(value)) return
    onVerify(value)
  }

  return (
    <div className="mt-[1.25rem]">
      <p className="mb-4 text-[0.875rem] text-gray-700">
        Enter the 6-digit code from your authenticator app for <span className="font-medium">{email}</span>.
      </p>

      <div aria-live="polite">{error && <Alert className="mb-4" type="error" showIcon title={error} />}</div>

      <Form layout="vertical" onFinish={() => submit(code)}>
        <Form.Item label="Authentication code">
          <AuthenticatorCodeInput
            onChange={setCode}
            onComplete={submit}
            disabled={loading}
            autoFocus
            resetKey={attempt}
          />
        </Form.Item>
        <Button
          htmlType="submit"
          loading={loading}
          disabled={!isCompleteAuthenticatorCode(code)}
          className={`${AUTH_PRIMARY_BUTTON_CLASS} w-full`}
        >
          Verify
        </Button>
        <Button type="link" className="mt-[0.5rem] w-full" disabled={loading} onClick={onBack}>
          Back to sign in
        </Button>
      </Form>
    </div>
  )
}
